import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar
 * @property {HTMLButtonElement} toggleButton
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLButtonElement} buyNowButton
 * @property {HTMLButtonElement} minusButton
 * @property {HTMLButtonElement} plusButton
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLElement} quantityDisplay
 * @property {HTMLElement} quantityNumber
 * @property {HTMLElement} purchasePanel
 * @property {HTMLElement} originalPrice
 * @property {HTMLElement} discountPrice
 * @property {HTMLElement} totalPrice
 * @property {HTMLElement} savingText
 * @property {HTMLImageElement} productImage
 */

/**
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = [
    'stickyBar',
    'toggleButton',
    'addToCartButton',
    'buyNowButton',
    'minusButton',
    'plusButton',
    'quantityInput',
    'quantityDisplay',
    'quantityNumber',
    'purchasePanel',
    'originalPrice',
    'discountPrice',
    'totalPrice',
    'savingText',
  ];

  #buyButtonsIntersectionObserver = null;
  #mainBottomObserver = null;
  #resetTimeout;
  #isStuck = true;
  #animationTimeout = null;
  #abortController = new AbortController();
  #targetAddToCartButton = null;
  #currentQuantity = 1;
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');

    window.addEventListener('pageshow', this.#handlePageReturn, { signal });
    window.addEventListener('focus', this.#handlePageReturn, { signal });
    document.addEventListener('visibilitychange', this.#handlePageReturn, { signal });

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener('sticky-cart:force-quantity', this.#handleForceQuantity, { signal });

    this.#getInitialQuantity();
    this.#restoreCollapsedState();
    this.#showStickyBar();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();

    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  #handlePageReturn = () => {
  if (document.visibilityState === 'hidden') return;

  this.#resetBuyNowButton();
};

  #resetBuyNowButton = () => {
    const buyNowButton = this.refs.buyNowButton;

    if (!buyNowButton) return;
    if (buyNowButton.dataset.buyNowLoading !== 'true') return;

    delete buyNowButton.dataset.buyNowLoading;

    buyNowButton.disabled = this.dataset.variantAvailable !== 'true';
    buyNowButton.textContent = 'Buy it now';

    this.#syncMainQuantityInput(this.#currentQuantity);
    this.#updateButtonText();
  };

  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    this.#isStuck = true;

    if (this.refs.stickyBar) {
      this.refs.stickyBar.dataset.stuck = 'true';
    }
  }

  handleToggleStickyCart = () => {
    const stickyBar = this.refs.stickyBar;
    const toggleButton = this.refs.toggleButton;

    if (!stickyBar || !toggleButton) return;

    const isCollapsed = stickyBar.dataset.collapsed === 'true';
    const nextCollapsed = !isCollapsed;

    stickyBar.dataset.collapsed = nextCollapsed ? 'true' : 'false';
    stickyBar.dataset.stuck = 'true';
    toggleButton.setAttribute('aria-expanded', nextCollapsed ? 'false' : 'true');

    try {
      localStorage.setItem('stickyCartCollapsed', nextCollapsed ? 'true' : 'false');
    } catch (error) {}
  };

  #restoreCollapsedState() {
    const stickyBar = this.refs.stickyBar;
    const toggleButton = this.refs.toggleButton;

    if (!stickyBar || !toggleButton) return;

    stickyBar.dataset.stuck = 'true';

    try {
      const savedCollapsed = localStorage.getItem('stickyCartCollapsed');

      if (savedCollapsed === 'true') {
        stickyBar.dataset.collapsed = 'true';
        toggleButton.setAttribute('aria-expanded', 'false');
      } else {
        stickyBar.dataset.collapsed = 'false';
        toggleButton.setAttribute('aria-expanded', 'true');
      }
    } catch (error) {
      stickyBar.dataset.collapsed = 'false';
      toggleButton.setAttribute('aria-expanded', 'true');
    }
  }

  handleStickyQuantityMinus = () => {
    this.#setStickyQuantity(this.#currentQuantity - this.#getStep());
  };

  handleStickyQuantityPlus = () => {
    this.#setStickyQuantity(this.#currentQuantity + this.#getStep());
  };

  handleStickyQuantityInput = () => {
    const value = parseInt(this.refs.quantityInput.value, 10) || this.#getMin();
    this.#setStickyQuantity(value);
  };

  #setStickyQuantity(quantity) {
    const min = this.#getMin();
    const max = this.#getMax();
    const step = this.#getStep();

    let nextQuantity = Math.max(min, quantity);

    if (max !== null) {
      nextQuantity = Math.min(max, nextQuantity);
    }

    if ((nextQuantity - min) % step !== 0) {
      nextQuantity = min + Math.round((nextQuantity - min) / step) * step;
    }

    this.#currentQuantity = nextQuantity;
    this.refs.quantityInput.value = String(nextQuantity);

    try {
      sessionStorage.setItem('stickyCartQuantity', String(nextQuantity));
      sessionStorage.setItem('stickyCartVariantId', String(this.dataset.currentVariantId || ''));
    } catch (error) {}

    this.#syncMainQuantityInput(nextQuantity);
    this.#updateButtonText();
  }

  #getMin() {
    return parseInt(this.refs.quantityInput.getAttribute('min') || '1', 10) || 1;
  }

  #getMax() {
    const max = this.refs.quantityInput.getAttribute('max');
    return max ? parseInt(max, 10) : null;
  }

  #getStep() {
    return parseInt(this.refs.quantityInput.getAttribute('step') || '1', 10) || 1;
  }

  #syncMainQuantityInput(quantity) {
    const productForm = this.#getProductForm();
    const quantityInput = productForm?.querySelector('input[name="quantity"]');

    if (!quantityInput) return;

    quantityInput.value = String(quantity);
    quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleBuyNowClick = async () => {
    const variantId = this.dataset.currentVariantId;
    const buyNowButton = this.refs.buyNowButton;

    if (!variantId || !buyNowButton || buyNowButton.disabled) return;

    const quantity = this.#currentQuantity || 1;

    this.#syncMainQuantityInput(quantity);
    this.#updateButtonText();

    try {
      sessionStorage.setItem('stickyCartQuantity', String(quantity));
      sessionStorage.setItem('stickyCartVariantId', String(variantId));
    } catch (error) {}

    buyNowButton.dataset.buyNowLoading = 'true';
    buyNowButton.disabled = true;
    buyNowButton.textContent = 'Loading...';

    try {
      const cartResponse = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      const cart = await cartResponse.json();

      const currentLine = cart.items?.find((item) => {
        return String(item.variant_id) === String(variantId);
      });

      const currentCartQuantity = currentLine ? Number(currentLine.quantity) || 0 : 0;

      let previousBuyNowQuantity = 0;

      try {
        const previousVariantId = sessionStorage.getItem('stickyBuyNowVariantId');
        const savedPreviousQuantity = parseInt(sessionStorage.getItem('stickyBuyNowQuantity') || '0', 10);

        if (String(previousVariantId) === String(variantId) && savedPreviousQuantity > 0) {
          previousBuyNowQuantity = savedPreviousQuantity;
        }
      } catch (error) {}

      const expectedAlreadyAdded = Math.max(currentCartQuantity, previousBuyNowQuantity);
      const quantityToAdd = Math.max(quantity - expectedAlreadyAdded, 0);

      if (quantityToAdd > 0) {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            id: Number(variantId),
            quantity: quantityToAdd,
          }),
        });
      }

      try {
        sessionStorage.setItem('stickyBuyNowVariantId', String(variantId));
        sessionStorage.setItem('stickyBuyNowQuantity', String(Math.max(quantity, expectedAlreadyAdded)));
      } catch (error) {}

      window.location.href = '/checkout';
    } catch (error) {
      delete buyNowButton.dataset.buyNowLoading;
      buyNowButton.disabled = false;
      buyNowButton.textContent = 'Buy it now';
      console.error(error);
    }
  };

  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;

    this.#syncMainQuantityInput(this.#currentQuantity);

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    const productImage = this.refs.productImage;

    if (!cartIcon || !this.refs.addToCartButton || !productImage) {
      if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

      this.#resetTimeout = setTimeout(() => {
        this.refs.addToCartButton.removeAttribute('data-added');
      }, 800);

      return;
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);

    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const currentCollapsed = this.refs.stickyBar.getAttribute('data-collapsed') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    this.refs.stickyBar.setAttribute('data-stuck', 'true');
    this.refs.stickyBar.setAttribute('data-collapsed', currentCollapsed);
    this.dataset.variantAvailable = variantAvailable;

    if (this.refs.toggleButton) {
      this.refs.toggleButton.setAttribute('aria-expanded', currentCollapsed === 'true' ? 'false' : 'true');
    }

    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    if (this.refs.quantityInput) {
      this.refs.quantityInput.value = String(this.#currentQuantity);
    }

    this.#syncMainQuantityInput(this.#currentQuantity);
    this.#updateButtonText();
    this.#showStickyBar();
  };

  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (!variantId) return;

    this.dataset.currentVariantId = variantId;
  };

  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';

    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);

    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');

    if (!selectedOptions) return;

    variantTitleElement.textContent = selectedOptions;
  };

  #handleCartAddComplete = (_event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;

    if (this.refs.quantityInput) {
      this.refs.quantityInput.value = String(this.#currentQuantity);
    }

    try {
      sessionStorage.setItem('stickyCartQuantity', String(this.#currentQuantity));
      sessionStorage.setItem('stickyCartVariantId', String(this.dataset.currentVariantId || ''));
    } catch (error) {}

    this.#syncMainQuantityInput(this.#currentQuantity);
    this.#updateButtonText();
  };

  #handleForceQuantity = (event) => {
  const quantity = parseInt(event.detail?.quantity || '1', 10);
  const variantId = event.detail?.variantId;

  if (!quantity || quantity < 1) return;

  if (variantId && String(variantId) !== String(this.dataset.currentVariantId)) return;

  this.#setStickyQuantity(quantity);

  if (this.refs.buyNowButton) {
    delete this.refs.buyNowButton.dataset.buyNowLoading;
    this.refs.buyNowButton.disabled = this.dataset.variantAvailable !== 'true';
    this.refs.buyNowButton.textContent = 'Buy it now';
  }
};

  #showStickyBar() {
    const { stickyBar } = this.refs;

    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  #hideStickyBar() {
    const { stickyBar } = this.refs;

    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');

    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  #getInitialQuantity() {
    let initialQuantity = parseInt(this.dataset.initialQuantity || '1', 10) || 1;

    try {
      const savedVariantId = sessionStorage.getItem('stickyCartVariantId');
      const savedQuantity = parseInt(sessionStorage.getItem('stickyCartQuantity') || '', 10);

      if (savedVariantId === String(this.dataset.currentVariantId) && savedQuantity > 0) {
        initialQuantity = savedQuantity;
      }
    } catch (error) {}

    this.#currentQuantity = initialQuantity;

    if (this.refs.quantityInput) {
      this.refs.quantityInput.value = String(this.#currentQuantity);
    }

    if (this.refs.buyNowButton) {
      this.refs.buyNowButton.disabled = this.dataset.variantAvailable !== 'true';
      this.refs.buyNowButton.textContent = 'Buy it now';
    }

    this.#syncMainQuantityInput(this.#currentQuantity);
    this.#updateButtonText();
  }

  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    quantityNumber.textContent = this.#currentQuantity.toString();

    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }

    this.#updateDiscountDisplay();
  }

  #updateDiscountDisplay() {
    const unitPriceCents = parseInt(this.refs.purchasePanel?.dataset.unitPriceCents || '0', 10);
    const quantity = this.#currentQuantity;

    if (!unitPriceCents || !quantity) return;

    const originalCents = unitPriceCents * quantity;
    const discountCents = this.#getDiscountCents(originalCents, quantity);
    const totalCents = Math.max(originalCents - discountCents, 0);

    this.refs.originalPrice.textContent = this.#formatMoney(originalCents);
    this.refs.discountPrice.textContent = `-${this.#formatMoney(discountCents)}`;
    this.refs.totalPrice.textContent = this.#formatMoney(totalCents);
    this.refs.savingText.textContent = this.#getSavingText(quantity);
  }

  #getDiscountCents(originalCents, quantity) {
    if (quantity >= 15) return Math.round(originalCents * 0.1);
    if (quantity >= 10) return 15000;
    if (quantity >= 8) return 10000;
    if (quantity >= 5) return 5000;

    return 0;
  }

  #getSavingText(quantity) {
    if (quantity < 5) {
      return `Add ${5 - quantity} more to save $50`;
    }

    if (quantity < 8) {
      return `Save $50 now · Add ${8 - quantity} more to save $100`;
    }

    if (quantity < 10) {
      return `Save $100 now · Add ${10 - quantity} more to save $150`;
    }

    if (quantity < 15) {
      return `Save $150 now · Add ${15 - quantity} more to get 10% Off`;
    }

    return `You get 10% Off`;
  }

  #formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}